import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { Button, CircularProgress, Typography } from '@mui/material'

import { Link } from 'react-router-dom'
import undescore from 'underscore'
import clsx from 'clsx'
import { useDebouncedCallback } from '@workspace/frontend-kit'
import useStyles from './_styles'
import useButtonStyles from '../../../styles/buttonsStyles'
import PlannedExpertiseHeader from './PlannedExpertiseHeader'
import PlannedExpertiseCard from './PlannedExpertiseCard'
import { PlannedExpertiseCardListProps } from './_props'
import { EmptyStateExpertiseImage, DisabledExpertiseImage } from '../../../assets/images/image'
import MessageState from '../../MessageState/MessageState'
import PageContent from '../../Common/pageContent/PageContent'
import PageContentBody from '../../Common/pageContent/PageContentBody'
import useCurrentHcs from '../../../hooks/useCurrentHcs'
import useCurrentUser from '../../../hooks/useCurrentUser'
import { useAddLogMutation } from '../../../api/mutations/__generated__/addLog.gql'
import { TransmissionForHcsDtoIds, useGetIdsHealthcareservicesHcsIdTransmissions } from '@toktokdoctech/tokapi-client'

const PlannedExpertiseCardList = ({ isPreview = false }: PlannedExpertiseCardListProps & { isPreview?: boolean }) => {
    const currentHcs = useCurrentHcs()
    const currentUser = useCurrentUser()
    const [limit, setLimit] = useState(isPreview ? 4 : 8)
    const [minContainerHeight, setMinContainerHeight] = useState(0)
    const [search, setSearch] = useState('')
    const [searchValue, setSearchValue] = useState('')
    const debouncedSearch = useDebouncedCallback((value: string) => setSearchValue(value), 300)
    const [totalDraftCount, setTotalDraftCount] = useState(0)

    const {
        data: transmissionsData,
        isLoading: isTransmissionsLoading,
        isFetching: isTransmissionsFetching,
    } = useGetIdsHealthcareservicesHcsIdTransmissions(
        currentHcs.id,
        {
            statuses: ['Draft'],
            search: searchValue || undefined,
            limit: isPreview ? 4 : limit,
        },
        {
            query: {
                select: (data) => {
                    const items = data?.items as any[] | undefined

                    if (!items || items.length === 0)
                        return {
                            items: [],
                            total: 0,
                        }
                    if (data?.total && totalDraftCount === 0) {
                        setTotalDraftCount(data.total)
                    }

                    return {
                        items: items
                            .map(
                                (transmission): TransmissionForHcsDtoIds => ({
                                    id: String(transmission.id ?? ''),
                                    transmitedAt: String(transmission.transmitedAt ?? ''),
                                    startedAt: String(transmission.startedAt ?? ''),
                                    patient: {
                                        id: String(transmission.patient?.id ?? ''),
                                        fullName: transmission.patient?.fullName ?? '',
                                        sex: transmission.patient?.sex ?? '',
                                        birthdate: transmission.patient?.birthdate ?? '',
                                        age: transmission.patient?.age ?? 0,
                                    },
                                    requiredExpert: {
                                        id: String(transmission.requiredExpert?.id ?? ''),
                                        fullName: transmission.requiredExpert?.fullName ?? '',
                                        speciality: transmission.requiredExpert?.speciality ?? '',
                                        profession: transmission.requiredExpert?.profession ?? '',
                                    },
                                    workflow: {
                                        id: String(transmission.workflow?.id ?? ''),
                                        name: transmission.workflow?.name ?? '',
                                    },
                                    nbDocuments: transmission.nbDocuments ?? 0,
                                    status: transmission.status ?? '',
                                }),
                            )
                            .sort(
                                (a, b) =>
                                    new Date(a.transmitedAt ?? 0).getTime() - new Date(b.transmitedAt ?? 0).getTime(),
                            ),

                        total: data?.total ?? items.length,
                    }
                },
            },
        },
    )

    const isCardListLoading = isTransmissionsLoading || isTransmissionsFetching
    const classes = useStyles()
    const buttonsClasses = useButtonStyles()
    const [sendLog] = useAddLogMutation()
    const cardsContainerRef = useRef<HTMLDivElement>(null)
    const filteredExpertises = transmissionsData?.items ?? []
    const totalExpertises = transmissionsData?.total ?? 0
    const hasFilters = useMemo(() => searchValue !== '', [searchValue])

    const handlePageHeightChange = useCallback(
        () =>
            undescore.debounce(() => {
                if (hasFilters) {
                    setMinContainerHeight(0)
                } else if (cardsContainerRef.current) {
                    setMinContainerHeight(cardsContainerRef.current.offsetHeight)
                }
            }, 300),
        [hasFilters],
    )

    useEffect(() => {
        handlePageHeightChange()
    }, [filteredExpertises.length, hasFilters, handlePageHeightChange])

    useEffect(() => {
        const handleResize = () => setMinContainerHeight(0)

        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    useEffect(() => {
        debouncedSearch(search)
    }, [search, debouncedSearch])

    useEffect(() => {
        sendLog({
            variables: {
                input: {
                    hcsId: currentHcs.id,
                    fromApp: 'IDS',
                    userId: currentUser.id,
                    message: `[PlannedExpertiseCardList] Accès liste téléexpertises planifiées`,
                    logLevel: 'INFO',
                },
            },
        })
    }, [currentHcs.id, sendLog, currentUser.id])

    const handleLoadMore = () => {
        setLimit(limit + 4)
    }

    const expertiseLengthCount = `Vous avez vu ${filteredExpertises.length} sur ${totalExpertises} téléexpertises.`

    const isDisabled = !currentHcs.isTransmissionEnabled

    const renderLoadMore = () => (
        <div className={classes.PlannedExpertiseCardsNoPreviewBottom}>
            {filteredExpertises.length < totalExpertises && (
                <div className={classes.PlannedExpertiseCardsCountContainer}>
                    <Typography className={classes.PlannedExpertiseCardsCount}>{expertiseLengthCount}</Typography>
                    <div className={classes.PlannedExpertiseCardsCountContainerLoadbar}>
                        <div
                            className={classes.PlannedExpertiseCardsCountContainerLoadbarProgress}
                            style={{
                                width: `${(filteredExpertises.length * 100) / totalExpertises}%`,
                            }}
                        />
                    </div>
                </div>
            )}

            {filteredExpertises.length < totalExpertises && (
                <Button onClick={handleLoadMore} className={buttonsClasses.ButtonBlack}>
                    Voir plus d’actes
                </Button>
            )}
            {filteredExpertises.length === totalExpertises && (
                <Typography className={classes.PlannedExpertiseCardsEndOfList}>Fin de la liste</Typography>
            )}
        </div>
    )

    const renderPreviewTitle = () => {
        let titleText
        if (!currentHcs.isTransmissionEnabled) {
            titleText = '0 téléexpertise à finaliser'
        } else if (totalExpertises > 0) {
            titleText = `${filteredExpertises.length} sur ${totalExpertises} ${
                totalExpertises === 1 ? 'affichée' : 'affichées'
            }`
        }
        return (
            <div className={classes.PlannedExpertiseHeaderTitleContainer}>
                <Typography className={classes.PlannedExpertiseHeaderTitle}>Téléexpertises à finaliser</Typography>
                {totalExpertises > 0 ? (
                    <div className={classes.PlannedExpertiseHeaderTitleContainerCount}>
                        <Typography className={classes.PlannedExpertiseCardListCountText}>{titleText}</Typography>
                        {currentHcs.isTransmissionEnabled && (
                            <Button
                                component={Link}
                                to="/teleexpertises"
                                className={classes.PlannedExpertiseHeaderTitleContainerCountButton}
                            >
                                Voir tout
                            </Button>
                        )}
                    </div>
                ) : null}
            </div>
        )
    }

    const renderContent = () => {
        if (isCardListLoading) {
            return (
                <div className={classes.PlannedExpertiseCardListLoading}>
                    <CircularProgress size="3.6rem" />
                    <Typography fontWeight={600} fontSize="1.6rem">
                        Chargement des téléexpertises à finaliser
                    </Typography>
                </div>
            )
        }
        if (isDisabled) {
            return (
                <MessageState
                    text="Cette fonctionnalité est désactivée sur votre établissement. Pour en savoir plus, contactez le support TokTokDoc."
                    image={<DisabledExpertiseImage />}
                />
            )
        }

        if (filteredExpertises.length > 0) {
            return (
                <>
                    <div className={classes.PlannedExpertiseCardsInner}>
                        {filteredExpertises.map((expertise) => (
                            <PlannedExpertiseCard key={expertise.id} expertise={expertise} isPreview={isPreview} />
                        ))}
                    </div>
                    <div
                        className={clsx(
                            classes.PlannedExpertiseCardsButonContainer,
                            !isPreview && classes.PlannedExpertiseCardsButonContainerMore,
                        )}
                    >
                        {isPreview && (
                            <Button
                                component={Link}
                                to="/teleexpertises"
                                className={buttonsClasses.ButtonTextBlackUnderlined}
                            >
                                Voir tout
                            </Button>
                        )}
                        {!isPreview && renderLoadMore()}
                    </div>
                </>
            )
        }

        const emptyText =
            totalExpertises === 0
                ? "Aucune téléexpertise à finaliser, les prochaines s'afficheront ici."
                : 'Aucune téléexpertise ne correspond à votre recherche.'

        return <MessageState text={emptyText} image={<EmptyStateExpertiseImage />} />
    }

    return (
        <PageContent>
            <PlannedExpertiseHeader
                setSearch={setSearch}
                search={search}
                isPreview={isPreview}
                previewTitle={renderPreviewTitle()}
                renderLeftHeader
                disabled={isDisabled}
            />
            <PageContentBody>
                <div
                    className={isPreview ? classes.PlannedExpertiseCardListMainContainer : undefined}
                    ref={cardsContainerRef}
                    style={{
                        minHeight: `${minContainerHeight}px`,
                    }}
                >
                    {renderContent()}
                </div>
            </PageContentBody>
        </PageContent>
    )
}

export default PlannedExpertiseCardList
